// ATTICUS-OPS-FOUNDATION-1 (2026-06-10) — Parties → Delivery Instructions sub-tab.
// Read-through of the assignor + slip + To/CC/BCC routing (all from Parties) + three
// editable fields (assignment scope/date + instructions) + a "Generate Acknowledgment
// of Assignment" button (WNW-themed PDF, opened in a new tab). Reuses the existing
// per-party routing — no duplicate billing-recipient concept.
import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";
import ComposeEmailModal from "./ComposeEmailModal";

export function DeliveryInstructions({ claimId, canEdit, workRefresh, onWorkChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ assignment_scope: "", assignment_date: "", delivery_instructions: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [compose, setCompose] = useState(null);
  const [busy, setBusy] = useState(false);
  // dirty = the user has typed into the editable fields since the last seed;
  // a background refresh (workRefresh bump, e.g. window refocus) must NOT
  // clobber in-progress edits — it only updates the derived read-through data.
  const dirty = useRef(false);
  const setF = (patch) => { dirty.current = true; setForm(f => ({ ...f, ...patch })); };

  const load = () => apiFetch(`/claims/${claimId}/delivery-instructions`).then((d) => {
    setData(d);
    if (!dirty.current) setForm({
      assignment_scope: d.assignment_scope || "",
      assignment_date: d.assignment_date || "",
      delivery_instructions: d.delivery_instructions || "",
    });
  }).catch(() => {});
  useEffect(() => { dirty.current = false; }, [claimId]);
  // Stale-data fix (2026-07-03): assignor / slip / routing here are DERIVED from
  // claim_parties, so re-fetch whenever the workspace refresh counter bumps (a
  // party was added/edited on a sibling lens) — not just on mount.
  useEffect(() => { load(); }, [claimId, workRefresh]);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await apiFetch(`/claims/${claimId}/delivery-instructions`, {
        method: "PATCH",
        body: {
          assignment_scope: form.assignment_scope || null,
          assignment_date: form.assignment_date || null,
          delivery_instructions: form.delivery_instructions || null,
        },
      });
      dirty.current = false;
      setMsg("Saved."); load();
      onWorkChanged && onWorkChanged();   // assignment scope/date feed docs + readiness elsewhere
    } catch { setMsg("Save failed."); } finally { setSaving(false); }
  };

  const generate = async () => {
    try {
      const res = await apiFetch(`/claims/${claimId}/acknowledgment-of-assignment.pdf`, { raw: true });
      if (!res.ok) return;
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* ignore */ }
  };

  const emailAoa = async () => {
    if (busy) return; setBusy(true); setMsg("");
    try {
      const r = await apiFetch(`/claims/${claimId}/acknowledgment-of-assignment/to-documents`, { method: "POST", body: {} });
      setCompose({ document_ids: [r.document_id], subject: "Acknowledgment of Assignment" });
    } catch (e) { setMsg(e.message || "Could not prepare the email."); }
    setBusy(false);
  };

  if (!data) return <div className="card"><div className="card-body" style={{ color: "var(--text2)" }}>Loading…</div></div>;
  const { assignor, slip = [], routing = [] } = data;
  const lbl = { fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 4 };
  const inp = { fontSize: 13, padding: "6px 8px", width: "100%", boxSizing: "border-box" };
  const th  = { textAlign: "left", padding: "5px 8px", fontSize: 11, color: "white", background: "var(--navy)", whiteSpace: "nowrap" };
  const td  = { padding: "5px 8px", borderBottom: "1px solid var(--border)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="card">
        <div className="card-header"><h3>Assigned By</h3></div>
        <div className="card-body" style={{ fontSize: 13 }}>
          {assignor ? (
            <div>
              <div style={{ fontWeight: 600 }}>{assignor.company_name || "—"}</div>
              <div style={{ color: "var(--text2)" }}>
                {assignor.contact_name || "(no contact)"}{assignor.contact_email ? ` · ${assignor.contact_email}` : ""}
              </div>
              {!assignor.has_contact && (
                <div style={{ color: "#b45309", fontSize: 12, marginTop: 4 }}>
                  ⚠ No contact at the assigning company — add one in the Parties sub-tab (the Acknowledgment needs it).
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "#b45309" }}>
              ⚠ No assignor designated. Mark the engaging party as <b>Assignor</b> in the Parties sub-tab.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Client Reference Details (Slip)</h3></div>
        <div className="card-body" style={{ overflowX: "auto" }}>
          {slip.length ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>
                <th style={th}>Company</th><th style={{ ...th, textAlign: "right" }}>Share</th>
                <th style={th}>Client Reference</th><th style={th}>Name</th><th style={th}>Email</th>
              </tr></thead>
              <tbody>{slip.map((s, i) => (
                <tr key={i}>
                  <td style={td}>{s.company_name || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "ui-monospace,monospace" }}>{s.participation_pct != null ? `${s.participation_pct}%` : "—"}</td>
                  <td style={td}>{s.reference_number || "—"}</td>
                  <td style={td}>{s.contact_name || "—"}</td>
                  <td style={td}>{s.contact_email || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <div style={{ color: "var(--text2)", fontSize: 12 }}>No financial parties on the slip yet — add them in the Parties sub-tab.</div>}
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Reporting, Billing &amp; Other Instructions</h3>
          <div style={{ display: "flex", gap: 6 }}>
            {/* Generate = render + open the PDF (a read; stays available to any
                viewer). ✉ Email FILES the PDF into the claim's Doc Center first,
                which is a claim document write — H17 gates it on canEdit so a
                read-only role can't create a document it would then be refused
                permission to delete. */}
            <button className="btn btn-blue btn-sm" onClick={generate}>Generate Acknowledgment of Assignment</button>
            {canEdit && <button className="btn btn-outline btn-sm" onClick={emailAoa} disabled={busy}>{busy ? "Preparing…" : "✉ Email"}</button>}
          </div>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {routing.length > 0 && (
            <div style={{ fontSize: 12, color: "var(--text2)" }}>
              <div style={lbl}>Current routing (from Parties)</div>
              {routing.map((r, i) => <div key={i}>· {r}</div>)}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={lbl}>Assignment Scope</div>
              <input style={inp} value={form.assignment_scope} disabled={!canEdit}
                onChange={(e) => setF({ assignment_scope: e.target.value })}
                placeholder="e.g. Emergency Response Oversight and Damage Survey" />
            </div>
            <div>
              <div style={lbl}>Date of Assignment</div>
              <input type="date" style={inp} value={form.assignment_date} disabled={!canEdit}
                onChange={(e) => setF({ assignment_date: e.target.value })} />
            </div>
          </div>
          <div>
            <div style={lbl}>Reporting, Billing &amp; Other Instructions</div>
            <textarea style={{ ...inp, minHeight: 90, fontFamily: "inherit" }} value={form.delivery_instructions} disabled={!canEdit}
              onChange={(e) => setF({ delivery_instructions: e.target.value })}
              placeholder="e.g. Submit reporting and invoicing to interested underwriters, c/o … attn …. Billing: $X/hr plus expenses." />
          </div>
          {canEdit && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              {msg && <span style={{ fontSize: 12, color: "var(--text2)" }}>{msg}</span>}
            </div>
          )}
        </div>
      </div>
      {compose && <ComposeEmailModal claimId={claimId} claim={data ? data.claim : null}
        prefill={{ subject: compose.subject, document_ids: compose.document_ids }}
        onClose={() => setCompose(null)} onSent={() => { setCompose(null); setMsg("Acknowledgment emailed."); }} />}
    </div>
  );
}
